import React from 'react';
import { InputAdornment, IconButton, InputProps, TextField } from '@material-ui/core';
import { DeleteText as Clear } from '../Icons';
import Downshift, { ControllerStateAndHelpers } from 'downshift';
import { SearchBoxSuggestions } from './SearchBoxSuggestion';
import { PALETTE } from '../../common/styles/palette';
import { debounce } from "lodash";

export interface ISearchableItem<T> {
    label: string;
    value: T;
}

interface ISearchBoxApiState<T> {
    error?: boolean;
    focused?: boolean;
    valueInput: string;
    helperText: string,
    items: Array<ISearchableItem<T>>;
    selectedItem?: ISearchableItem<T>;
}

type ISearchBoxApiProps<T> = {
    selectedItem?: T;
    templateItem?: (item?: T) => ISearchableItem<T> | undefined;
    getItems: (value: string, take?: number) => Promise<Array<T>>;
    listSize?: number;
    doFilter?: boolean;
    openOnFocus?: boolean;
    required?: boolean;
    helperText?: string;
    onSelectedChange?: (selectedItem?: T) => void;
    /*TextFieldProps*/
    label?: string;
    onValueSubmit?: (item?: T) => void;
    onBlur?: (item?: T) => any;
    placeholder?: string;
    disabled?: boolean;
    InputProps?: Partial<InputProps>;
    clearItem?: (callback: any) => void;
    minCharacterNumber?: number;
}

const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
};

const defaultItemsToTake = 10;
const defaultHelperText = "Inserire almeno {n} caratteri";
const defaultCharacterNumber = 3;

export class SearchBoxApi<T> extends React.Component<ISearchBoxApiProps<T>, ISearchBoxApiState<T>> {

    searchBoxContainerRef: React.RefObject<any>;
    timeoutFunc: any;
    minCharLength: number = defaultCharacterNumber;

    constructor(p: any, c: any) {
        super(p);
        this.searchBoxContainerRef = React.createRef();
        this.state = {
            error: false,
            focused: false,
            valueInput: this.getInitialValue(),
            items: [],
            helperText: undefined,
            selectedItem: this.getTemplateItem(this.props.selectedItem),
        }

        if (this.props.minCharacterNumber)
            this.minCharLength = this.props.minCharacterNumber;

        this.timeoutFunc = undefined;

        if (this.props.clearItem)
            this.props.clearItem(this.clearItem);
    }

    getTemplateItem = (item: any) => {
        return this.props.templateItem ? this.props.templateItem(item) : undefined
    }

    componentDidUpdate(prevProps: ISearchBoxApiProps<T>) {

        if (this.props.minCharacterNumber)
            this.minCharLength = this.props.minCharacterNumber;

        if (prevProps.selectedItem !== undefined && this.props.selectedItem === undefined) {
            // this.clearItem();

            this.setState({
                valueInput: '',
                helperText: defaultHelperText.replace("{n}", this.minCharLength.toString()),
                items: [],
                selectedItem: undefined
            })
        }
    }

    getInitialValue = () => {
        let value = '';
        let item = this.getTemplateItem(this.props.selectedItem);
        if (item)
            value = item.label;
        return value;
    }

    onSelect = (selectedItem: ISearchableItem<T>, stateAndHelpers: ControllerStateAndHelpers<ISearchableItem<T>>) => {
        if (this.props.onSelectedChange) {
            this.props.onSelectedChange(selectedItem ? selectedItem.value : undefined);
        }
        let valueInput = selectedItem ? selectedItem.label : "";
        this.setState({
            valueInput: valueInput,
            selectedItem: selectedItem
        });
    }

    onOuterClick = (stateAndHelpers: ControllerStateAndHelpers<any>) => {
        // let selectedItem = this.props.selectedItem;
        let selectedItem = this.getTemplateItem(this.props.selectedItem);
        let valueInput = selectedItem ? selectedItem.label : "";

        this.setState({
            valueInput: valueInput,
            helperText: defaultHelperText.replace("{n}", this.minCharLength.toString()),
            selectedItem
        });
    }

    debouncedSearch = debounce(async (val) => {
        console.log("debounce ", val)
        this.reloadItems(val);
    }, 600);

    // debouncedSearch2 = React.useRef(
    //     debounce(async (val) => {
    //     //   setCharacters(await search(val));
    //     await this.reloadItems(val);
    //     }, 300)
    //   ).current;

    onInputChange = (e: any, helper: ControllerStateAndHelpers<ISearchableItem<T>>) => {
        let val = (e.target as any).value as string;
        console.log("onInputChange ", val)
        let indexSelected = undefined;
        if (val === '' && !indexSelected) {
            helper.clearSelection()
            indexSelected = 0;
        }
        let filteredItems = this.filterSuggestions(val);
        if (!indexSelected) {
            indexSelected = this.props.doFilter ? 0 : filteredItems.findIndex(
                (item: any) => { return item.label.toUpperCase().indexOf(val.toUpperCase()) === 0 });
        }

        helper.setHighlightedIndex(indexSelected);

        if (val.length >= this.minCharLength) {
            // this.timeoutFunc = setTimeout(() => { this.reloadItems(val) }, 400);
            this.debouncedSearch(val);
        } else {
            if (this.state.items.length > 0) {
                this.setState({
                    items: [],
                    helperText: undefined,
                })
            }
        }

        this.setState({
            valueInput: val,
        });
    }

    itemToString = (item: ISearchableItem<T>) => item ? item.label : '';

    onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, helper: ControllerStateAndHelpers<ISearchableItem<T>>) => {
        switch (event.key) {
            case 'Enter': {
                if (this.props.onValueSubmit) {
                    event.preventDefault();
                    if (helper.highlightedIndex !== undefined) {
                        this.props.onValueSubmit(this.state.items[helper.highlightedIndex].value);
                    }
                }
                helper.closeMenu();
                break;
            }
            case 'ArrowUp':
            case 'ArrowDown': {
                //lascio che si possa effettuare la selezione con le frecce
                break;
            }
            case 'Escape': {
                event.preventDefault();
                //set text to selected item or empty in so one is 
                let val = helper.selectedItem ? helper.selectedItem.label : "";
                this.setState({
                    valueInput: val,
                });
                break;
            }
            default: {
                // helper.setHighlightedIndex(undefined);
            }
        }
    }

    onFocus = (e: React.FocusEvent<any>, helper: ControllerStateAndHelpers<ISearchableItem<T>>) => {
        if (this.props.openOnFocus)
            helper.openMenu();
        helper.setHighlightedIndex(0);
        this.setState({ focused: true, error: false })
    }

    private onBlurEvent = (e: any, helper: ControllerStateAndHelpers<ISearchableItem<T>>) => {
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
        let value = helper.selectedItem ? helper.selectedItem.label : "";
        this.setState({
            focused: false,
            valueInput: value,
            selectedItem: helper.selectedItem ? helper.selectedItem : undefined
        });
    }

    private hasValue = (v?: string) => (v && v !== undefined);

    //Per ora decisiamo di visualizzare tutti i contenuti. I filtri avvengono solo a livello di API
    filterSuggestions = (valueInput?: string) => {
        return this.limitSuggestionsSize(
            this.props.doFilter ?
                this.filterSuggestionsLabel(valueInput)
                : this.state.items)
    }

    filterSuggestionsLabel = (valueInput?: string) => {
        if (valueInput) {
            try {
                return this.state.items.filter(i => (i.label.toUpperCase()).indexOf((valueInput.toUpperCase())) >= 0);
            } catch {
                console.error('Eccezione nel filtro della label');
            }
        }
        return this.state.items;
    }

    limitSuggestionsSize = (items: Array<ISearchableItem<T>>) => {
        return (!this.props.listSize || items.length < this.props.listSize) ? items : items.slice(0, this.props.listSize);
    }

    getSerchableItems = (items: Array<T>) => {
        let array: Array<ISearchableItem<T>> = [];
        for (let i = 0; i < items.length; i++) {
            let item = this.getTemplateItem(items[i]);
            if (item) {
                array.push(item)
            }
        }
        return array;
    }

    reloadItems = (value: string) => {
        this.props.getItems(value, this.props.listSize ? this.props.listSize : defaultItemsToTake)
            .then(array => {
                this.setState({
                    items: this.getSerchableItems(array),
                    helperText: array.length === 0 ? 'Nessun dato trovato' : '',
                })
            }).catch(err => {
                console.error("api", err)
                this.setState({
                    items: [],
                    helperText: 'Errore',
                })
            })
    }

    clearItem = () => {
        console.info('clearItem called');

        if (this.props.onSelectedChange) {
            this.props.onSelectedChange(undefined);
        }

        this.setState({
            valueInput: '',
            helperText: defaultHelperText.replace("{n}", this.minCharLength.toString()),
            items: [],
            selectedItem: undefined
        })
    }

    endIcon = (props: ISearchBoxApiProps<T>) => {
        if (props.disabled)
            return null;
        return (<InputAdornment position="end" variant='filled'>
            <IconButton aria-label="clear"
                onClick={(e) => this.clearItem()}
                onMouseDown={handleMouseDown}
                disableFocusRipple
                size='small'
                color='secondary'>
                <Clear style={{
                    width: '12px',
                    height: '12px',
                    color: PALETTE.UI.InputText.ClearIconColor,
                    fill: PALETTE.UI.InputText.ClearIconColor
                }} />
            </IconButton >
        </InputAdornment>)
    }

    public render() {
        //const { ...otherProps } = this.props;
        return (
            <Downshift
                inputValue={this.state.valueInput}
                onSelect={this.onSelect}
                onOuterClick={this.onOuterClick}
                itemToString={this.itemToString}
                selectedItem={this.state.selectedItem ?? null}

            >

                {(helper: ControllerStateAndHelpers<ISearchableItem<T>>) => (
                    <div style={{ width: '100%', position: "relative" }}
                    >
                        <div ref={this.searchBoxContainerRef}>
                            <TextField
                                onFocus={e => this.onFocus(e, helper)}
                                onBlur={e => this.onBlurEvent(e, helper)}
                                label={this.props.label}
                                disabled={this.props.disabled}
                                fullWidth
                                color="secondary"
                                margin={"normal"}
                                placeholder={this.props.placeholder}
                                value={this.state.valueInput}
                                helperText={this.state.helperText}
                                InputProps={helper.getInputProps({
                                    onChange: (e: any) => this.onInputChange(e, helper),
                                    endAdornment: this.endIcon(this.props),
                                    onBlur: (e: any) => this.onBlurEvent(e, helper)
                                }) as any}
                                onKeyDown={(e) => this.onKeyDown(e, helper)}
                            />
                        </div>
                        <SearchBoxSuggestions<T>
                            helper={helper}
                            items={this.state.items}
                            limitSize={this.props.listSize ? this.props.listSize : defaultItemsToTake}
                            doFilter={this.props.doFilter ? this.props.doFilter : false}
                            searchBoxContainerRef={this.searchBoxContainerRef}
                        />
                    </div>
                )}
            </Downshift >
        );
    }
}